import React, { useEffect, useState } from 'react';
import { getAgentVersion, downloadAgent } from '../api';
import { Boxes, Download, CheckCircle2, ShieldCheck, RefreshCw, Upload as UploadIcon, Trash2, HardDriveDownload } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../components/Button';

const FEATURES = [
  { icon: RefreshCw,        title: 'Plugin, theme & core updates',   sub: 'One-click and bulk updates from the dashboard' },
  { icon: HardDriveDownload,title: 'Deploy custom .zip (download)',   sub: 'Sites fetch the file themselves — bypasses upload firewalls' },
  { icon: ShieldCheck,      title: 'Secure token auth',              sub: 'Every request checked against the site token (X-WPM-Token)' },
  { icon: Trash2,           title: 'Elementor form cleanup',         sub: 'Permanently delete submissions on a schedule' },
];

export default function Agent() {
  const [version, setVersion] = useState(null);

  useEffect(() => {
    getAgentVersion().then(r => setVersion(r.version || null)).catch(() => {});
  }, []);

  const handleDownload = async () => {
    try {
      const blob = await downloadAgent();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `wp-manager-agent-v${version || '2'}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Downloading latest agent…');
    } catch {
      toast.error('Download failed');
    }
  };

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Agent</h1>
          <p>The plugin that connects each WordPress site to this dashboard</p>
        </div>
      </div>

      <div className="page-content">
        <div className="two-col">
          {/* LEFT — download + info */}
          <div>
            <div className="card">
              <div className="card-head"><h3><Boxes size={14}/> WP Manager Agent</h3></div>
              <div style={{ padding: '18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
                  <div style={{ width: 52, height: 52, borderRadius: 14, background: 'var(--grad)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 20px var(--accent-glow)', flexShrink: 0 }}>
                    <Boxes size={26} color="#fff" />
                  </div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>WP Manager Agent</div>
                    <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
                      Latest version: {version
                        ? <span className="badge green" style={{ marginLeft: 4 }}>v{version}</span>
                        : <span style={{ color: 'var(--text3)' }}>…</span>}
                    </div>
                  </div>
                </div>

                <p style={{ fontSize: 12.5, color: 'var(--text2)', lineHeight: 1.7, marginBottom: 16 }}>
                  Install this small plugin on each WordPress site you want to manage. It exposes a secure API so the dashboard can update plugins/themes/core, deploy custom .zip files, and clean up Elementor form submissions — all remotely.
                </p>

                <Button className="btn primary" onClick={handleDownload} style={{ padding: '11px 18px', fontSize: 13 }}>
                  <Download size={15} /> Download latest agent{version ? ` (v${version})` : ''}
                </Button>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 10 }}>
                  A fresh <code>.zip</code> is generated every time — always the newest version bundled with this dashboard.
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-head"><h3><CheckCircle2 size={14}/> What it does</h3></div>
              <div style={{ padding: '6px 0' }}>
                {FEATURES.map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '11px 18px', borderBottom: i < FEATURES.length - 1 ? '1px solid var(--border)' : 'none' }}>
                    <div style={{ width: 34, height: 34, borderRadius: 10, background: 'var(--accent-bg)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <f.icon size={17} />
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{f.title}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--text3)', marginTop: 1 }}>{f.sub}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT — install / update instructions */}
          <div>
            <div className="card">
              <div className="card-head"><h3><UploadIcon size={14}/> Install on a new site</h3></div>
              <div style={{ padding: '14px 18px', fontSize: 12.5, color: 'var(--text2)', lineHeight: 1.9 }}>
                <ol style={{ margin: 0, paddingLeft: 18 }}>
                  <li>Download the agent (button on the left).</li>
                  <li>On the WordPress site: <strong>Plugins → Add New → Upload Plugin</strong>.</li>
                  <li>Choose the <code>.zip</code>, click <strong>Install Now</strong>, then <strong>Activate</strong>.</li>
                  <li>Go to <strong>Settings → WP Manager</strong>, click <strong>Generate New Token</strong>, and copy the token + Site URL.</li>
                  <li>In this dashboard, open <strong>My Sites → Connect new site</strong> and paste them.</li>
                </ol>
              </div>
            </div>

            <div className="card">
              <div className="card-head"><h3><RefreshCw size={14}/> Update an existing site</h3></div>
              <div style={{ padding: '14px 18px', fontSize: 12.5, color: 'var(--text2)', lineHeight: 1.9 }}>
                <div style={{ marginBottom: 10 }}>
                  <strong>Fastest — all sites at once:</strong> use <strong>My Sites → “Update agents on all sites”</strong>. The agent is replaced in place, so sites stay connected and no re-connect is needed.
                </div>
                <div>
                  <strong>Manually on one site:</strong> download the latest, upload it via <strong>Plugins → Add New → Upload Plugin</strong>, and choose <strong>“Replace current with uploaded”</strong>. The token stays intact.
                </div>
              </div>
            </div>

            <div className="card" style={{ borderColor: 'var(--accent2)', background: 'var(--accent-bg)' }}>
              <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
                <ShieldCheck size={16} color="var(--accent)" />
                <div style={{ flex: 1, fontSize: 12, color: 'var(--accent-text)' }}>
                  The agent contains no secrets — the token is generated on each site and never shipped in the download.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
